import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from "./ConstantApi";
import Types from "../Helpers/Interfaces/ProductsType";

// Define types for the API
export interface TaxData {
  profileName: string;
  profileCode: string;
  isActive: boolean;
  isDefault: boolean;
  description: string;
  taxDetails: TaxDetail[];
  products: string[];
}
interface TaxDetail {
  // countryTaxId: string;
  countryCode: string;
  taxType: string;
  taxRate: number;
  
}
export interface TaxResponse {
  _id: string;
  profileName: string;
  profileCode: string;
  isActive: boolean;
  profileDescription: string;
  taxDetails: TaxDetail[];
  createdAt: string;
  updatedAt: string;
  status: string;
  isDefault: boolean;
  taxRate: number;
  taxType: string;
}

export interface TaxListResponse {
  data: TaxResponse[];
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  status: string;
}

export interface SearchResponse {
  pagination: any;
  products: TaxResponse[];
}

export interface TaxListParams {
  page: number;
  pageSize: number;
  filters?: any[];
  sortBy?: string;
  sortOrder?: string;
  search?: string;
  isActive?: string;
  countryCode?: string;
}

export interface UpdateTaxRequest {
  id?: string;
  data: any;
}

export interface DeleteTaxResponse {
  message: string;
  status: string;
}

export const TaxationService = createApi({
  reducerPath: "Taxation",
  baseQuery: RtkBaseQueryWithReauth(process.env.REACT_APP_BASE_URL || ""),
  tagTypes: ["Taxation"],
  endpoints: (builder) => ({
    getTaxProfile: builder.query<TaxListResponse, TaxListParams>({
      query: (params) => ({
        url: `${ApiPath.getTaxList}?page=${params.page}&pageSize=${params.pageSize}`,
        method: "GET",
      }),
      transformResponse: (response: { data: any }) => response.data,
      providesTags: ["Taxation"],
    }),
    getTaxProfiles: builder.query<TaxListResponse, TaxListParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        queryParams.append("page", params.page.toString());
        queryParams.append("pageSize", params.pageSize.toString());

        if (params.search) {
          queryParams.append("search", params.search);
        }

        if (params.sortBy) {
          queryParams.append("sortBy", params.sortBy);
        }

        if (params.sortOrder) {
          queryParams.append("sortOrder", params.sortOrder);
        }

        if (params.isActive) {
          queryParams.append("isActive", params.isActive);
        }
        if(params.countryCode){
          queryParams.append("countryCode", params.countryCode);
        }

        if (Array.isArray(params.filters)) {
          params.filters.forEach((filter) => {
            if (filter.key && filter.value !== undefined) {
              queryParams.append(
                `filters[${filter.key}]`,
                String(filter.value)
              );
            }
          });
        }

        return {
          url: `${ApiPath.getTaxList}?${queryParams.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Taxation"],
    }),

    getTaxProfileDetails: builder.query<TaxData, string>({
      query: (id) => ({
        url: `${ApiPath.getTaxDetails}/${id}`,
        method: "GET",
      }),
      transformResponse: (response: { data: TaxData }) => response.data,
      providesTags: ["Taxation"],
    }),

    createAndUpdateTaxProfile: builder.mutation<
      TaxListResponse,
      UpdateTaxRequest
    >({
      query: (payload) => {
        if ("id" in payload) {
          return {
            url: `${ApiPath.updateTax}/${payload.id}`,
            method: "PUT",
            body: payload.data,
          };
        }

        return {
          url: ApiPath.createTax,
          method: "POST",
          body: payload.data,
        };
      },
      invalidatesTags: ["Taxation"],
    }),

    deleteTaxDetails: builder.mutation<DeleteTaxResponse, string>({
      query: (id) => ({
        url: `${ApiPath.deleteTax}/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response: { data: DeleteTaxResponse }) => response.data,
      invalidatesTags: ["Taxation"],
    }),
  }),
});

export const {
  useGetTaxProfileQuery,
  useLazyGetTaxProfilesQuery,
  useCreateAndUpdateTaxProfileMutation,
  useDeleteTaxDetailsMutation,
  useGetTaxProfileDetailsQuery,
} = TaxationService;
